'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * =============================================================================
 * useScrollDirection Hook
 * =============================================================================
 *
 * A custom React hook that detects scroll direction and provides state for
 * implementing "smart" sticky headers that hide on scroll down and show on
 * scroll up.
 *
 * =============================================================================
 * HOW IT WORKS
 * =============================================================================
 *
 * 1. SCROLL TRACKING
 *    - Listens to the window 'scroll' event
 *    - Compares current scroll position with the last recorded position
 *    - Uses a threshold to prevent jittery behavior from tiny scroll movements
 *
 * 2. DIRECTION DETECTION
 *    - If scrollY increases by more than threshold → scrolling DOWN
 *    - If scrollY decreases by more than threshold → scrolling UP
 *    - Small movements (< threshold) are ignored to prevent flickering
 *
 * 3. STATE MANAGEMENT
 *    - `scrollDirection`: 'up' | 'down' | null (null = no significant scroll)
 *    - `isAtTop`: true when page is at or near the top (within threshold)
 *    - `scrollY`: current scroll position (useful for advanced animations)
 *
 * =============================================================================
 * USAGE EXAMPLE
 * =============================================================================
 *
 * ```tsx
 * function Header() {
 *   const { scrollDirection, isAtTop } = useScrollDirection({ threshold: 10 });
 *
 *   // Show search bar when:
 *   // 1. User is at the top of the page, OR
 *   // 2. User is scrolling UP (wants to go back/search)
 *   const showSearchBar = isAtTop || scrollDirection === 'up';
 *
 *   return (
 *     <header className="sticky top-0">
 *       {/* Main header - always visible *\/}
 *       <div className="flex items-center justify-between">
 *         <Logo />
 *         <LoginButton />
 *       </div>
 *
 *       {/* Search bar - conditionally visible with animation *\/}
 *       <div
 *         className={`
 *           transition-all duration-300 ease-in-out overflow-hidden
 *           ${showSearchBar ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}
 *         `}
 *       >
 *         <SearchBar />
 *       </div>
 *     </header>
 *   );
 * }
 * ```
 *
 * =============================================================================
 * REPLICATING ON OTHER SCREENS
 * =============================================================================
 *
 * To use this pattern on other screens (e.g., product listing, category pages):
 *
 * 1. Import the hook:
 *    import { useScrollDirection } from '@/presentation/hooks/useScrollDirection';
 *
 * 2. Use it in your component:
 *    const { scrollDirection, isAtTop } = useScrollDirection();
 *
 * 3. Create a computed visibility state:
 *    const showElement = isAtTop || scrollDirection === 'up';
 *
 * 4. Apply to your element with CSS transitions:
 *    - Use `max-h-0` / `max-h-[value]` for height animation
 *    - Use `opacity-0` / `opacity-100` for fade effect
 *    - Use `transform -translate-y-full` / `translate-y-0` for slide effect
 *    - Always add `transition-all duration-300` for smooth animation
 *
 * =============================================================================
 * CONFIGURATION OPTIONS
 * =============================================================================
 *
 * @param {number} threshold - Minimum scroll distance (in pixels) before
 *   direction change is registered. Higher = less sensitive. Default: 10
 *
 * @param {number} topThreshold - Distance from top (in pixels) to consider
 *   "at top". Useful for triggering "at top" state slightly before reaching 0.
 *   Default: 10
 *
 * =============================================================================
 * PERFORMANCE NOTES
 * =============================================================================
 *
 * - Uses passive event listener for better scroll performance
 * - Debounces rapid scroll events using threshold
 * - Cleans up event listener on unmount
 * - Uses refs to avoid unnecessary re-renders
 *
 * =============================================================================
 */

export type ScrollDirection = 'up' | 'down' | null;

export interface UseScrollDirectionOptions {
  /**
   * Minimum scroll distance (in pixels) before direction change is registered.
   * Higher values = less sensitive, prevents jittery behavior.
   * @default 10
   */
  threshold?: number;

  /**
   * Distance from top (in pixels) to consider "at top of page".
   * @default 10
   */
  topThreshold?: number;
}

export interface UseScrollDirectionResult {
  /**
   * Current scroll direction: 'up', 'down', or null (no significant movement)
   */
  scrollDirection: ScrollDirection;

  /**
   * Whether the page is at or near the top (within topThreshold)
   */
  isAtTop: boolean;

  /**
   * Current scroll Y position in pixels
   */
  scrollY: number;
}

export function useScrollDirection(
  options: UseScrollDirectionOptions = {}
): UseScrollDirectionResult {
  const { threshold = 10, topThreshold = 10 } = options;

  // State for scroll direction and position
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
  const [scrollY, setScrollY] = useState(0);
  const [isAtTop, setIsAtTop] = useState(true);

  // Ref to track the last scroll position (avoids state updates for comparison)
  const lastScrollY = useRef(0);

  // Ref to track if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';

  const handleScroll = useCallback(() => {
    if (!isBrowser) return;

    const currentScrollY = window.scrollY;

    // Update scroll position state
    setScrollY(currentScrollY);

    // Check if at top of page
    setIsAtTop(currentScrollY <= topThreshold);

    // Calculate scroll difference
    const scrollDiff = currentScrollY - lastScrollY.current;

    // Only update direction if scroll exceeds threshold
    // This prevents jittery behavior from tiny scroll movements
    if (Math.abs(scrollDiff) >= threshold) {
      if (scrollDiff > 0) {
        // Scrolling DOWN (positive difference)
        setScrollDirection('down');
      } else {
        // Scrolling UP (negative difference)
        setScrollDirection('up');
      }

      // Update last scroll position
      lastScrollY.current = currentScrollY;
    }
  }, [isBrowser, threshold, topThreshold]);

  useEffect(() => {
    if (!isBrowser) return;

    // Set initial values
    lastScrollY.current = window.scrollY;
    setScrollY(window.scrollY);
    setIsAtTop(window.scrollY <= topThreshold);

    // Add scroll listener with passive option for better performance
    // Passive listeners can't call preventDefault(), which allows the browser
    // to optimize scrolling performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isBrowser, handleScroll, topThreshold]);

  return {
    scrollDirection,
    isAtTop,
    scrollY,
  };
}

export default useScrollDirection;
